import type { PickResult } from '../constants';

/**
 * Scoring is entirely league-configurable. The defaults match the brief
 * (win 1, loss 0, push 0.5) but nothing in the codebase hardcodes them — standings,
 * weekly scores and the grader all read the season's configured values.
 */
export interface ScoringConfig {
  winPoints: number;
  lossPoints: number;
  pushPoints: number;
}

export const DEFAULT_SCORING: ScoringConfig = {
  winPoints: 1,
  lossPoints: 0,
  pushPoints: 0.5,
};

/**
 * Points for one pick.
 *
 * `pending` and `void` both score zero, but they mean different things and are counted
 * differently in standings: a pending game has not happened yet, while a voided game was
 * pulled by the commissioner and is excluded from the games-played denominator so that no
 * player is penalised for an administrative action.
 */
export function pointsForPick(result: PickResult, config: ScoringConfig): number {
  switch (result) {
    case 'win':
      return config.winPoints;
    case 'loss':
      return config.lossPoints;
    case 'push':
      return config.pushPoints;
    case 'pending':
    case 'void':
      return 0;
  }
}

export interface StandingsTotals {
  wins: number;
  losses: number;
  pushes: number;
  points: number;
  /** Picks the player actually submitted on graded, non-voided games. */
  picksMade: number;
  /** Graded, non-voided games available to the player — the honest denominator. */
  picksAvailable: number;
}

/**
 * Win percentage counts a push as half a win, the standard ATS convention, and excludes
 * pushes from the denominator only in the sense that they are already halved. Games the
 * player never picked count as losses against them, otherwise skipping a week would look
 * better than picking badly.
 */
export function winPercentage(totals: StandingsTotals): number {
  const decided = totals.wins + totals.losses + totals.pushes;
  if (decided === 0) return 0;
  return (totals.wins + totals.pushes * 0.5) / decided;
}

/** Picks the player never submitted on games that have already been graded. */
export function missedPicks(totals: StandingsTotals): number {
  return Math.max(0, totals.picksAvailable - totals.picksMade);
}

/**
 * Format points for display. Half points are real (a push is 0.5) so we keep one decimal
 * only when it is meaningful: `7` not `7.0`, but `7.5` stays `7.5`.
 */
export function formatPoints(points: number): string {
  return points % 1 === 0 ? String(points) : points.toFixed(1);
}

export function formatRecord(totals: Pick<StandingsTotals, 'wins' | 'losses' | 'pushes'>): string {
  return totals.pushes > 0
    ? `${totals.wins}-${totals.losses}-${totals.pushes}`
    : `${totals.wins}-${totals.losses}`;
}

export function formatWinPercentage(totals: StandingsTotals): string {
  const pct = winPercentage(totals);
  // Sports convention: .625 rather than 62.5%
  return pct.toFixed(3).replace(/^0/, '');
}
